import * as React from "react";
import { API } from "General/API";
import { API_URL } from "General/APIEndpoints";
import { withTranslation, WithTranslation } from "react-i18next";
import { ButtonsRow } from "Components/Form/ButtonsRow";
import { Tabs, Row, Input, Tag, Alert, Typography, Space, Select } from "antd";
import { I18N_NS } from "Config/i18n";
import { Loader } from "Components/General/Loader";
import { notificationSuccess } from "utils/libHelpers/AntdNotification";
import { Button } from "Components/General/Button";
import { withRouter, WithRouterProps } from "Components/General/WithRouter";
import { Table } from "Components/General/Table";

interface FilenameConventionProps extends WithTranslation, WithRouterProps {}

interface FilenameConventionState {
  value: string;
  fnMetadata: API_AdminMetadataDefinitionPreview[];
  loading: boolean;
  fnTestMdId: number;
  fnTestMdValue: string;
  fnTestName: string;
  testResults: API_FilenameTestResult;
}

export const FilenameConvention = withTranslation([I18N_NS.ADMIN, I18N_NS.PROJECT])(
  withRouter(
    class extends React.Component<FilenameConventionProps, FilenameConventionState> {
      constructor(props: FilenameConventionProps) {
        super(props);

        this.state = {
          value: "",
          loading: false,
          fnTestName: "",
          fnTestMdId: null,
          fnTestMdValue: "",
          testResults: {},
          fnMetadata: [],
        };
      }

      componentDidMount() {
        (async () => {
          await this.fetchFnXml();
          await this.fetchFnMetadata();
        })();
      }

      getProjectId() {
        const projectId: number = +this.props.params.id;
        if (Number.isNaN(projectId)) {
          throw new Error("Project id is invalid");
        }
        return projectId;
      }

      handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        this.setState({ value: e.target.value });
      };

      async fetchFnXml() {
        try {
          this.setState({ loading: true });
          const projectId = this.getProjectId();
          const resp = await API.request(API_URL.ADMIN_PROJECTS.getFileNameConventions(projectId), { method: "GET" });
          this.setState({ value: resp });
        } catch (error) {
          throw error;
        } finally {
          this.setState({ loading: false });
        }
      }

      async fetchFnMetadata() {
        try {
          const projectId = this.getProjectId();
          const fnMetadata: API_AdminMetadataDefinitionPreview[] = await API.getData(
            API_URL.ADMIN_PROJECTS.getActiveMetadataDefinitions(projectId)
          );
          this.setState({ fnMetadata });
        } catch (error) {
          throw error;
        }
      }

      testFileName = async () => {
        try {
          const projectId = this.getProjectId();
          const body: API_FilenameTestRequest = {
            definitionId: this.state.fnTestMdId,
            filename: this.state.fnTestName,
            metadataValue: this.state.fnTestMdValue,
          };
          const response: API_FilenameTestResult = await API.newData(
            API_URL.ADMIN_PROJECTS.testFilename(projectId),
            body
          );
          if (response) {
            this.setState({ testResults: response });
          }
        } catch (error) {
          throw error;
        }
      };

      update = async () => {
        try {
          const newValue = this.state.value;
          this.setState({ loading: true });
          const projectId = this.getProjectId();
          await API.editData(API_URL.ADMIN_PROJECTS.getFileNameConventions(projectId), newValue, {});
          this.setState({ value: newValue });
          const { t } = this.props;
          notificationSuccess(t("Success"), t("Filename convention is saved"));
        } catch (error) {
          throw error;
        } finally {
          this.setState({ loading: false });
        }
      };

      handleAbort = () => {
        this.setState({
          fnTestName: "",
          fnTestMdId: null,
          fnTestMdValue: "",
          fnMetadata: [],
          testResults: {},
        });
      };

      render() {
        const { loading, value, fnTestName, testResults } = this.state;
        const { t } = this.props;

        if (!value && loading) {
          return <Loader />;
        }

        return (
          <div className="g-card">
            <h1>{t("Filename management")}</h1>
            <Tabs
              defaultActiveKey="1"
              items={[
                {
                  key: "1",
                  label: t("Filename check according to plan key"),
                  children: (
                    <>
                      <Input.TextArea onChange={this.handleInputChange} value={value} autoSize={{ minRows: 4 }} />
                      <ButtonsRow>
                        <Button onClick={this.update} type="primary" loading={loading}>
                          {this.props.t("Save")}
                        </Button>
                      </ButtonsRow>
                    </>
                  ),
                },
                {
                  key: "2",
                  label: t("Test filename"),
                  children: (
                    <>
                      <Row justify="space-between" align="middle">
                        <Row justify="start" align="middle" style={{ flex: 1 }}>
                          <span className="mr-md">{t("File name")}:</span>
                          <Input
                            style={{ flex: 1 }}
                            className="flex mr-md"
                            value={fnTestName}
                            onChange={({ target: { value } }) => this.setState({ fnTestName: value })}
                          />
                        </Row>
                        <Row justify="start" align="middle">
                          <span className="mr-md">{t("Default metadata")}:</span>
                          <Select
                            className="mr-md"
                            allowClear
                            style={{ width: 200 }}
                            onChange={(v) => this.setState({ fnTestMdId: +v })}
                            placeholder={t("Select a metadata")}
                          >
                            {this.state.fnMetadata?.length > 0 &&
                              this.state.fnMetadata.map((m) => (
                                <Select.Option value={m.definitionId}>{m.definitionName}</Select.Option>
                              ))}
                          </Select>
                          <Input
                            className="mr-md"
                            style={{ width: 200 }}
                            onChange={(e) => this.setState({ fnTestMdValue: e.target.value })}
                            value={this.state.fnTestMdValue}
                            placeholder={t("Provide a value")}
                          />
                        </Row>
                        <Row justify="center" align="middle">
                          <Button
                            disabled={!fnTestName.length}
                            onClick={this.testFileName}
                            type="primary"
                            className="mr-md"
                          >
                            {t("Test")}
                          </Button>
                          <Button onClick={this.handleAbort} grey>
                            {t("Abort")}
                          </Button>
                        </Row>
                      </Row>
                      {Object.prototype.hasOwnProperty.call(testResults, "success") && !testResults.success ? (
                        <Alert title={testResults.message} type="error" showIcon className="mt-1" />
                      ) : (
                        <>
                          {testResults.message && (
                            <Alert title={testResults.message} type="success" showIcon className="mt-2 mb-md" />
                          )}
                          {testResults.tests && (
                            <Table<API_FilenameTestEntry>
                              name="fnTests"
                              size="small"
                              className="mb-lg"
                              pagination={false}
                              title={() => <Typography.Text strong>{t("Tests")}</Typography.Text>}
                              dataSource={testResults.tests}
                              columns={[
                                { title: t("Name"), dataIndex: "name" },
                                { title: t("Format"), dataIndex: "format" },
                                { title: t("Value"), dataIndex: "value" },
                                {
                                  title: t("Status"),
                                  dataIndex: "status",
                                  render: (value) =>
                                    value ? <Tag color="green">{t("OK")}</Tag> : <Tag color="red">{t("ERROR")}</Tag>,
                                },
                              ]}
                            />
                          )}
                          {testResults.check && (
                            <Table<{ key: string; value: string }>
                              name="fnCheck"
                              size="small"
                              className="mb-lg"
                              pagination={false}
                              title={() => (
                                <Space>
                                  <Typography.Text strong>{t(testResults.check.title)}</Typography.Text>
                                  {testResults.check.status ? (
                                    <Tag color="green">{t("OK")}</Tag>
                                  ) : (
                                    <Tag color="red">{t("ERROR")}</Tag>
                                  )}
                                </Space>
                              )}
                              dataSource={Object.keys(testResults.check.values).map((v) => ({
                                key: v,
                                value: testResults.check.values[v],
                              }))}
                              columns={[{ dataIndex: "key" }, { dataIndex: "value" }]}
                            />
                          )}
                          {testResults.automapping && (
                            <Table<{ value: string }>
                              name="fnAutomapping"
                              size="small"
                              className="mb-lg"
                              pagination={false}
                              title={() => <Typography.Text strong>{t(testResults.automapping.title)}</Typography.Text>}
                              dataSource={[{ value: testResults.automapping.value }]}
                              columns={[{ dataIndex: "value" }]}
                            />
                          )}
                          {testResults.rules && (
                            <Table<{ value: string }>
                              name="fnRules"
                              size="small"
                              className="mb-lg"
                              pagination={false}
                              title={() => <Typography.Text strong>{t(testResults.rules.title)}</Typography.Text>}
                              dataSource={testResults.rules.values.map((value) => ({ value }))}
                              columns={[{ dataIndex: "value" }]}
                            />
                          )}
                        </>
                      )}
                    </>
                  ),
                },
              ]}
            />
          </div>
        );
      }
    }
  )
);
